import { toRealtimeEvent, type RealtimeEvent, type RealtimeTransport } from './events';
import type { FocusState } from './message';
import { createEntityLocks, type EntityLocks, type LockOutcome } from './entity-locks';
import { createEntityPublisher } from './entity-publisher';
import { addMember, applyMessage, expirePeers, HEARTBEAT_MS, removeMember, syncMembers } from './peers';
import { createPeerStore, type PeerStore } from './peer-store';

const SWEEP_MS = 5_000;

/** One consumer's hold on a shared entity session. */
export type EntityPresence = {
  /** Everyone else on the entity, including the user's other tabs. */
  store: PeerStore;
  /** Publishes this tab's focus on the entity; null clears it. */
  setFocus(field: string | null, label?: string): void;
  /**
   * Locks on the entity. They belong to the shared session, not to this handle: any handle may
   * release a lock another one took, and the last `release` frees everything this tab holds.
   */
  locks: Pick<EntityLocks, 'getTable' | 'subscribe' | 'lock' | 'takeOver' | 'release'>;
  /** Clears focus this handle set and drops the hold; the last release unsubscribes. */
  release(): void;
};

type Session = {
  store: PeerStore;
  locks: EntityLocks;
  focus(focus: FocusState, owner: symbol): void;
  clearFocusOf(owner: symbol): void;
  refs: number;
  close(): void;
};

const NOT_ACQUIRED: LockOutcome = { acquired: false, holder: null };

const sessions = new WeakMap<RealtimeTransport, Map<string, Session>>();

function openSession(transport: RealtimeTransport, channel: string): Session {
  const store = createPeerStore();
  const update = store.update;
  // Entity channels carry focus only; the server refuses anything else.
  const publisher = createEntityPublisher((message) => transport.publish(channel, message));
  const locks = createEntityLocks(transport, channel);
  let focusOwner: symbol | null = null;

  // The presence answer is a snapshot from before it was sent: members who left meanwhile must
  // not come back with it, and members who joined or spoke meanwhile must not be dropped by it.
  const leftDuringFetch = new Set<string>();
  const heardDuringFetch = new Set<string>();
  const refreshPresence = async () => {
    leftDuringFetch.clear();
    heardDuringFetch.clear();
    const members = await transport.presence(channel).catch(() => null);
    if (members === null) return;
    const others = members.filter((m) => m.clientId !== transport.clientId() && !leftDuringFetch.has(m.clientId));
    update((current) => syncMembers(current, others, Date.now(), heardDuringFetch));
  };

  const onEvent = (event: RealtimeEvent) => {
    if (event.kind === 'subscribed') {
      void refreshPresence();
      locks.onSubscribed();
    } else if (event.kind === 'message' && event.message.type === 'lock.changed') {
      locks.applyChange(event.message.data);
    } else if (event.kind === 'message') {
      const { from, message } = event;
      if (from !== null && from.clientId !== transport.clientId()) {
        heardDuringFetch.add(from.clientId);
        update((current) => applyMessage(current, from, message, Date.now()));
      }
    } else if (event.member.clientId !== transport.clientId()) {
      if (event.kind === 'leave') {
        leftDuringFetch.add(event.member.clientId);
        heardDuringFetch.delete(event.member.clientId);
        update((current) => removeMember(current, event.member.clientId));
      } else {
        leftDuringFetch.delete(event.member.clientId);
        heardDuringFetch.add(event.member.clientId);
        update((current) => addMember(current, event.member, Date.now()));
        // Whoever just arrived has missed what we said before; tell them where we are.
        publisher.announce();
      }
    }
  };

  const unsubscribe = transport.subscribe(channel, (raw) => {
    const event = toRealtimeEvent(raw);
    if (event !== null) onEvent(event);
  });
  void refreshPresence();
  const sweep = setInterval(() => update((current) => expirePeers(current, Date.now())), SWEEP_MS);
  const heartbeat = setInterval(() => publisher.announce(), HEARTBEAT_MS);

  return {
    store,
    locks,
    refs: 0,
    focus(focus, owner) {
      focusOwner = focus.field === null ? null : owner;
      publisher.focus(focus);
    },
    clearFocusOf(owner) {
      if (focusOwner !== owner) return;
      focusOwner = null;
      publisher.focus({ field: null });
    },
    close() {
      clearInterval(sweep);
      clearInterval(heartbeat);
      locks.close();
      unsubscribe();
    },
  };
}

/**
 * Joins an entity channel. Handles for the same transport and channel share one subscription,
 * peer store and publisher; the last `release` leaves the channel.
 */
export function createEntityPresence(transport: RealtimeTransport, channel: string): EntityPresence {
  const forTransport = sessions.get(transport) ?? new Map<string, Session>();
  sessions.set(transport, forTransport);
  let session = forTransport.get(channel);
  if (session === undefined) {
    session = openSession(transport, channel);
    forTransport.set(channel, session);
  }
  session.refs += 1;

  const held = session;
  const owner = Symbol('entity-presence');
  let released = false;
  return {
    store: held.store,
    locks: {
      getTable: held.locks.getTable,
      subscribe: held.locks.subscribe,
      lock: async (field) => (released ? NOT_ACQUIRED : held.locks.lock(field)),
      takeOver: async (field) => (released ? NOT_ACQUIRED : held.locks.takeOver(field)),
      release: async (field) => (released ? undefined : held.locks.release(field)),
    },
    setFocus(field, label) {
      if (released) return;
      held.focus(field === null ? { field: null } : label === undefined ? { field } : { field, label }, owner);
    },
    release() {
      if (released) return;
      released = true;
      held.clearFocusOf(owner);
      held.refs -= 1;
      if (held.refs > 0) return;
      forTransport.delete(channel);
      void held.locks.releaseAll();
      held.close();
    },
  };
}
